import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase/client.js';
import type { Tip } from '../../types/tip.js';
import type { Visibility } from '../../types/visibility.js';
import { toTip } from './converters.js';

const COL = 'tips';

export interface TipInput {
  title: string;
  body: string;
  tags: string[];
  projectId?: string | undefined;
  visibility: Visibility;
}

export async function findShared(opts?: { count?: number }): Promise<Tip[]> {
  const q = query(
    collection(db, COL),
    where('visibility', '==', 'shared'),
    orderBy('createdAt', 'desc'),
    limit(opts?.count ?? 50),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => toTip(d as never));
}

export async function findByOwner(uid: string, opts?: { count?: number }): Promise<Tip[]> {
  const q = query(
    collection(db, COL),
    where('createdBy', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(opts?.count ?? 100),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => toTip(d as never));
}

export async function findById(id: string): Promise<Tip | null> {
  const snap = await getDoc(doc(db, `${COL}/${id}`));
  if (!snap.exists()) return null;
  return toTip(snap as never);
}

export async function create(uid: string, input: TipInput): Promise<string> {
  const data: DocumentData = {
    ...input,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COL), data);
  return ref.id;
}

export async function updateVisibility(id: string, visibility: Visibility): Promise<void> {
  await updateDoc(doc(db, `${COL}/${id}`), {
    visibility,
    updatedAt: serverTimestamp(),
  });
}

export async function update(id: string, patch: Partial<TipInput>): Promise<void> {
  // ignoreUndefinedProperties: true 対策。projectId を外す操作を反映する。
  const payload: DocumentData = { ...patch, updatedAt: serverTimestamp() };
  if ('projectId' in patch && patch.projectId === undefined) {
    payload['projectId'] = deleteField();
  }
  await updateDoc(doc(db, `${COL}/${id}`), payload);
}

export async function remove(id: string): Promise<void> {
  await deleteDoc(doc(db, `${COL}/${id}`));
}
